/**
 * 統一バリデーション関数ライブラリ
 * 全てのフォームで共通利用するバリデーション機能を提供
 */

import { isValidEmail } from './emailValidation';

/**
 * メールアドレス形式の厳格なバリデーション
 * emailValidation.tsの統一バリデーション関数を使用
 * 空文字列の場合は無効とする（後方互換性のため）
 */
export const validateEmail = (email: string): boolean => {
  if (!email || !email.trim()) return false;
  return isValidEmail(email);
};

/**
 * メールアドレスバリデーション用のエラーメッセージ
 */
export const EMAIL_VALIDATION_MESSAGE = '有効なメールアドレスを入力してください';

/**
 * 必須フィールドのバリデーション
 */
export const validateRequired = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (typeof value === 'string' && !value.trim()) return false;
  return true;
};

/**
 * 必須フィールド用のエラーメッセージ
 */
export const REQUIRED_VALIDATION_MESSAGE = 'この項目は必須です';

/**
 * 電話番号形式のバリデーション
 */
export const validatePhone = (phone: string): boolean => {
  if (!phone || !phone.trim()) return true; // 空の場合は有効（必須チェックは別途）
  const phoneRegex = /^[\d\-().\s]+$/;
  return phoneRegex.test(phone);
};

/**
 * 電話番号バリデーション用のエラーメッセージ
 */
export const PHONE_VALIDATION_MESSAGE = '有効な電話番号を入力してください';

/**
 * 最小文字数バリデーション
 */
export const validateMinLength = (value: string, minLength: number): boolean => {
  if (!value) return true; // 空の場合は有効（必須チェックは別途）
  return value.length >= minLength;
};

/**
 * 最大文字数バリデーション
 */
export const validateMaxLength = (value: string, maxLength: number): boolean => {
  if (!value) return true; // 空の場合は有効（必須チェックは別途）
  return value.length <= maxLength;
};

/**
 * 数値形式のバリデーション
 */
export const validateNumeric = (value: string): boolean => {
  if (!value || !value.trim()) return true; // 空の場合は有効（必須チェックは別途）
  return !isNaN(Number(value));
};

/**
 * 正の数値バリデーション
 */
export const validatePositiveNumber = (value: string): boolean => {
  if (!value || !value.trim()) return true; // 空の場合は有効（必須チェックは別途）
  const num = Number(value);
  return !isNaN(num) && num > 0;
};

/**
 * 統一バリデーション関数のタイプ定義
 */
export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

/**
 * メールアドレスの統一バリデーション（結果オブジェクト付き）
 */
export const validateEmailWithResult = (email: string): ValidationResult => {
  const isValid = validateEmail(email);
  return {
    isValid,
    message: isValid ? undefined : EMAIL_VALIDATION_MESSAGE,
  };
};

/**
 * 必須フィールドの統一バリデーション（結果オブジェクト付き）
 */
export const validateRequiredWithResult = (value: unknown): ValidationResult => {
  const isValid = validateRequired(value);
  return {
    isValid,
    message: isValid ? undefined : REQUIRED_VALIDATION_MESSAGE,
  };
};

/**
 * 複数バリデーションの組み合わせ実行
 */
export const validateField = (
  value: unknown,
  validations: ((value: unknown) => ValidationResult)[]
): ValidationResult => {
  for (const validation of validations) {
    const result = validation(value);
    if (!result.isValid) {
      return result;
    }
  }
  return { isValid: true };
};

/**
 * よく使われるバリデーションパターンのコンビネーション
 */
export const commonValidations = {
  /**
   * 必須メールアドレス
   */
  requiredEmail: (value: unknown): ValidationResult => {
    // 必須チェック
    if (!validateRequired(value)) {
      return validateRequiredWithResult(value);
    }
    // メール形式チェック
    if (typeof value === 'string') {
      return validateEmailWithResult(value);
    }
    return { isValid: false, message: EMAIL_VALIDATION_MESSAGE };
  },

  /**
   * オプショナルメールアドレス（空の場合は有効）
   */
  optionalEmail: (value: unknown): ValidationResult => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return { isValid: true };
    }
    if (typeof value === 'string') {
      return validateEmailWithResult(value);
    }
    return { isValid: false, message: EMAIL_VALIDATION_MESSAGE };
  },

  /**
   * 必須テキスト
   */
  requiredText: (value: unknown): ValidationResult => {
    return validateRequiredWithResult(value);
  },
};
